import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";

const SORTS = [
  { v: "newest", l: "Newest" },
  { v: "price_asc", l: "Price: Low to High" },
  { v: "price_desc", l: "Price: High to Low" },
];

export default function Shop() {
  const { category } = useParams();
  const [params, setParams] = useSearchParams();
  const search = params.get("search") || "";
  const selectOnly = params.get("select") === "true";

  const [categories, setCategories] = useState([]);
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("newest");
  const [page, setPage] = useState(1);
  const pageSize = 24;

  useEffect(() => { api.get("/categories").then((r) => setCategories(r.data)).catch(() => {}); }, []);
  useEffect(() => { setPage(1); }, [category, search, selectOnly, sort]);

  useEffect(() => {
    setLoading(true);
    api.get("/products", {
      params: {
        category: category || undefined,
        search: search || undefined,
        select: selectOnly || undefined,
        sort, page, page_size: pageSize,
      },
    }).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [category, search, selectOnly, sort, page]);

  const activeCat = useMemo(() => categories.find((c) => c.slug === category), [categories, category]);
  const totalPages = Math.max(1, Math.ceil(data.total / pageSize));

  const title = search ? `Results for "${search}"` : selectOnly ? "CardScience Select™" : activeCat ? activeCat.name : "Shop All Cards";

  return (
    <div className="bg-ink min-h-screen">
      <div className="relative border-b border-[color:var(--line)] overflow-hidden noise">
        <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(60% 100% at 100% 0%, rgba(155,92,255,0.25), transparent 60%)" }} />
        <div className="relative mx-auto max-w-[1400px] px-5 py-14">
          <p className="text-xs text-[color:var(--chrome-3)] mb-3">
            <Link to="/" className="hover:text-white">Home</Link> / <span className="text-[color:var(--chrome-2)]">{title}</span>
          </p>
          <h1 className="h-display text-4xl sm:text-5xl text-chrome">{title}</h1>
          {activeCat?.tagline && <p className="text-[color:var(--chrome-2)] mt-3">{activeCat.tagline}</p>}
        </div>
      </div>

      <div className="mx-auto max-w-[1400px] px-5 py-8">
        {/* filter row */}
        <div className="flex flex-wrap items-center gap-2 mb-8">
          <Link to="/shop" data-testid="filter-all"
            className={`px-4 py-2 rounded-full text-xs font-tech tracking-wide border transition-colors ${!category && !selectOnly ? "bg-white text-black border-white" : "border-[color:var(--line)] text-[color:var(--chrome-2)] hover:text-white"}`}>
            ALL
          </Link>
          {categories.map((c) => (
            <Link key={c.slug} to={`/shop/${c.slug}`} data-testid={`filter-${c.slug}`}
              className={`px-4 py-2 rounded-full text-xs font-tech tracking-wide border transition-colors ${category === c.slug ? "bg-white text-black border-white" : "border-[color:var(--line)] text-[color:var(--chrome-2)] hover:text-white"}`}>
              {c.name.toUpperCase()}
            </Link>
          ))}
          <Link to="/shop?select=true" data-testid="filter-select"
            className={`px-4 py-2 rounded-full text-xs font-tech tracking-wide border transition-colors ${selectOnly ? "select-badge border-transparent" : "border-[color:var(--line)] text-[color:var(--chrome-2)] hover:text-white"}`}>
            SELECT™
          </Link>

          <div className="ml-auto flex items-center gap-2">
            <SlidersHorizontal size={15} className="text-[color:var(--chrome-3)]" />
            <select data-testid="sort-select" value={sort} onChange={(e) => setSort(e.target.value)}
              className="bg-[color:var(--charcoal)] border border-[color:var(--line)] rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-[color:var(--cyan)]">
              {SORTS.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
        </div>

        <p className="text-xs text-[color:var(--chrome-3)] mb-6">{data.total} {data.total === 1 ? "card" : "cards"}</p>

        {loading ? (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[...Array(8)].map((_, i) => <div key={i} className="aspect-[3/4] rounded-2xl bg-[color:var(--charcoal)] animate-pulse" />)}
          </div>
        ) : data.items.length === 0 ? (
          <div className="text-center py-24">
            <p className="h-display text-2xl text-chrome mb-3">NO CARDS FOUND</p>
            <p className="text-[color:var(--chrome-3)]">Try a different search or browse all categories.</p>
            <Link to="/shop" className="btn-ghost inline-block rounded-lg px-6 py-3 text-xs mt-6">BROWSE ALL</Link>
          </div>
        ) : (
          <div data-testid="product-grid" className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {data.items.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        )}

        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12">
            <button disabled={page === 1} onClick={() => setPage((p) => p - 1)}
              className="btn-ghost rounded-lg h-10 w-10 flex items-center justify-center disabled:opacity-30"><ChevronLeft size={18} /></button>
            <span className="font-tech text-sm text-[color:var(--chrome-2)] px-4">{page} / {totalPages}</span>
            <button disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}
              className="btn-ghost rounded-lg h-10 w-10 flex items-center justify-center disabled:opacity-30"><ChevronRight size={18} /></button>
          </div>
        )}
      </div>
    </div>
  );
}
